import { ICONS_TYPES } from '@components/Icon';
import toastMessage from '@components/Toast';
import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteValue,
  TextField,
  createFilterOptions,
} from '@mui/material';
import {
  AutocompleteOwnerState,
  AutocompleteRenderGetTagProps,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
} from '@mui/material/Autocomplete/Autocomplete';
import { isDefAndNotNull } from '@utils/def';
import { isArray, isObject } from 'lodash';
import React, {
  HTMLAttributes,
  MouseEvent,
  SyntheticEvent,
  UIEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import OptionsListbox from './OptionsListbox';
import { LOAD_MORE_SCROLL_OFFSET } from './constants';
import { Sizes } from './enums';
import {
  AttentionIcon,
  AutoCompleteContainer,
  ErrorMessage,
  ErrorMessageContainer,
  Label,
  Option,
  RequiredMark,
  Root,
  StyledCheckBox,
  StyledChip,
} from './styles';
import {
  MultipleAutocompleteResponse,
  OptionType,
  OptionsPaging,
  Props,
  SingleAutocompleteResponse,
} from './types';

const AutoComplete = <
  Options extends OptionType,
  Multi extends boolean = false,
>({
  className,
  name,
  options = [],
  optionsWithCheckbox = false,
  enableShiftRangeSelect = false,
  keepInputOnSelect = false,
  limitOptions = 100,
  label,
  showLabel = false,
  placeholder,
  value,
  onChange,
  onChangeCallback,
  onInputChange,
  multiple,
  loading = false,
  disableClearable = false,
  disableCloseOnSelect = false,
  readOnly = false,
  disabled = false,
  required = false,
  size = Sizes.medium,
  fullWidth = false,
  noOptionsText = 'No options',
  isOptionEqualToValue,
  limitTags = 1,
  getLimitTagsText,
  getOptionDisabled,
  getOptionLabel,
  error,
  showErrorText = true,
  openOnRender = false,
  renderInput,
  renderOption,
  renderTags,
  groupBy,
  renderGroup,
  slotProps,
  postfix,
}: Props<Options, Multi>) => {
  type TypedValue = Multi extends true ? Options[] : Options | null;
  const disabledOrLoading = useMemo(
    () => disabled || loading,
    [disabled, loading],
  );
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Index (within the currently displayed option list) of the last option the
  // user clicked — the anchor a subsequent shift + click ranges from.
  const shiftAnchorIndexRef = useRef<number | null>(null);
  // Options are revealed `limitOptions` at a time so a huge list never renders
  // at once, while the user can still page through all of it.
  const [visibleOptionsCount, setVisibleOptionsCount] = useState(limitOptions);
  // Controlled input text, only used when `keepInputOnSelect` is on, so the
  // typed query survives a selection instead of MUI clearing it.
  const [inputValue, setInputValue] = useState('');
  const optionsPagingRef = useRef<OptionsPaging>({ total: 0, shown: 0 });
  // The exact option list currently rendered in the listbox.
  const displayedOptionsRef = useRef<Options[]>([]);

  const getLabelFromOptions = useCallback(
    (value: string | number): string => {
      const searchedLabel = options.filter(
        (option) => String(option.value) === String(value),
      );
      return searchedLabel.length ? searchedLabel[0].label : '';
    },
    [options],
  );
  const getValueFromOptions = useCallback(
    (label: string | number) => {
      const searchedValue = options.filter(
        (option) =>
          option.label.toLowerCase() === label.toString().toLowerCase(),
      );
      return searchedValue.length ? searchedValue[0].value : '';
    },
    [options],
  );
  const matchOptions = useMemo(() => createFilterOptions<Options>(), []);
  const handleShowMoreOptions = useCallback(
    () => setVisibleOptionsCount((current) => current + limitOptions),
    [limitOptions],
  );
  const handleListboxScroll = useCallback(
    (event: UIEvent<HTMLUListElement>) => {
      const listbox = event.currentTarget;
      const { total, shown } = optionsPagingRef.current;
      const reachedBottom =
        listbox.scrollHeight - listbox.scrollTop - listbox.clientHeight <=
        LOAD_MORE_SCROLL_OFFSET;

      if (reachedBottom && shown < total) handleShowMoreOptions();
    },
    [handleShowMoreOptions],
  );

  const fullSingleValue: SingleAutocompleteResponse = useMemo(() => {
    if (typeof value === 'string' || typeof value === 'number') {
      const labelFromOptions = getLabelFromOptions(value);

      return labelFromOptions === ''
        ? null
        : ({
            value: value,
            label: getLabelFromOptions(value),
          } as Options);
    } else if (typeof value === 'object' && value && !Array.isArray(value)) {
      const labelFromOptions = getLabelFromOptions(value.value);

      return labelFromOptions === ''
        ? null
        : ({
            ...value,
            label: getLabelFromOptions(value.value),
          } as Options);
    }
    return null;
  }, [getLabelFromOptions, value]);
  const fullMultipleValue: MultipleAutocompleteResponse = useMemo(() => {
    if (Array.isArray(value)) {
      return value
        .filter((el) => el != null)
        .map((el) => {
          if (typeof el === 'string' || typeof el === 'number') {
            return { value: el, label: getLabelFromOptions(el) } as Options;
          } else {
            return { ...el, label: getLabelFromOptions(el.value) } as Options;
          }
        })
        .filter((el) => el.label !== '');
    }
    return [];
  }, [getLabelFromOptions, value]);
  const fullValue = useMemo(
    () => (multiple ? fullMultipleValue : fullSingleValue),
    [fullMultipleValue, fullSingleValue, multiple],
  );

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      event.preventDefault();
      const pasteData = event.clipboardData?.getData('text');

      if (pasteData) {
        const pasteItems = pasteData
          .split(',')
          .map((item) => item.trim())
          .filter((item) => item !== '');

        const successItems: OptionType[] = [];
        const failedItems: string[] = [];

        for (let i = 0; i < pasteItems.length; i++) {
          const value = getValueFromOptions(pasteItems[i]);
          if (value === '') {
            failedItems.push(pasteItems[i]);
          } else {
            successItems.push({ value: value, label: pasteItems[i] });
          }
        }

        if (multiple) {
          const newState = new Map(
            [...fullMultipleValue, ...successItems].map((item) => [
              item.value,
              item,
            ]),
          );

          onChange(Array.from(newState.values()) as TypedValue);
          onChangeCallback?.(Array.from(newState.values()) as TypedValue);
        } else {
          if (successItems.length) {
            onChange(successItems[0] as TypedValue);
            onChangeCallback?.(successItems[0] as TypedValue);
          }
        }

        if (failedItems.length)
          toastMessage.warning(
            `Such values '${failedItems.join(', ')}' do not exist in the list`,
          );
      }
    },
    [
      fullMultipleValue,
      getValueFromOptions,
      multiple,
      onChange,
      onChangeCallback,
    ],
  );
  const defaultRenderInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        {...params}
        name={name}
        label={showLabel ? undefined : label}
        error={!!error}
        onPaste={handlePaste}
        placeholder={placeholder}
        required={required}
        inputRef={inputRef}
      />
    ),
    [error, handlePaste, label, name, placeholder, required, showLabel],
  );
  const defaultGetOptionLabel = useCallback(
    (option: Options) => option?.label || '',
    [],
  );
  const defaultIsOptionEqualToValue = useCallback(
    (option: Options, value: Options) =>
      option.value === value.value && !!value.label,
    [],
  );
  const defaultGetLimitTagsText = useCallback(
    (more: number) => <StyledChip label={`+${more}`} />,
    [],
  );
  const defaultRenderOption = useCallback(
    (props: HTMLAttributes<HTMLLIElement>, option: Options) => (
      <Option {...props} key={option.value}>
        {option.label}
      </Option>
    ),
    [],
  );
  const checkboxRenderOption = useCallback(
    (
      props: HTMLAttributes<HTMLLIElement>,
      option: Options,
      { selected }: AutocompleteRenderOptionState,
    ) => {
      return (
        <Option {...props} key={option.value}>
          <StyledCheckBox checked={selected} label={option.label} />
        </Option>
      );
    },
    [],
  );
  const baseRenderOption = useMemo(() => {
    if (renderOption) return renderOption;

    return optionsWithCheckbox ? checkboxRenderOption : defaultRenderOption;
  }, [
    checkboxRenderOption,
    defaultRenderOption,
    optionsWithCheckbox,
    renderOption,
  ]) as NonNullable<Props<Options, Multi>['renderOption']>;

  // Applies the clicked option's resulting state to every option between the
  // anchor and the clicked one, matching the usual list shift-select behaviour.
  const selectOptionRange = useCallback(
    (anchorIndex: number, clickedIndex: number, clickedSelected: boolean) => {
      const displayedOptions = displayedOptionsRef.current;
      const start = Math.min(anchorIndex, clickedIndex);
      const end = Math.max(anchorIndex, clickedIndex);
      const range = displayedOptions.slice(start, end + 1);
      const shouldSelect = !clickedSelected;

      const nextValue = new Map(
        fullMultipleValue.map((item) => [item.value, item]),
      );
      range.forEach((option) => {
        if (shouldSelect) {
          nextValue.set(option.value, option);
        } else {
          nextValue.delete(option.value);
        }
      });

      const newValue = Array.from(nextValue.values()) as TypedValue;
      onChange?.(newValue);
      onChangeCallback?.(newValue);
    },
    [fullMultipleValue, onChange, onChangeCallback],
  );
  const shiftRangeRenderOption = useCallback(
    (
      props: HTMLAttributes<HTMLLIElement>,
      option: Options,
      state: AutocompleteRenderOptionState,
      ownerState: AutocompleteOwnerState<Options, Multi, boolean, false>,
    ) => {
      const { onClick, ...restProps } = props;

      const handleClick = (event: MouseEvent<HTMLLIElement>) => {
        const anchorIndex = shiftAnchorIndexRef.current;

        if (
          event.shiftKey &&
          anchorIndex !== null &&
          anchorIndex !== state.index
        ) {
          event.preventDefault();
          selectOptionRange(anchorIndex, state.index, state.selected);
        } else {
          onClick?.(event);
        }

        shiftAnchorIndexRef.current = state.index;
      };

      return baseRenderOption(
        { ...restProps, onClick: handleClick },
        option,
        state,
        ownerState,
      );
    },
    [baseRenderOption, selectOptionRange],
  );
  const finalRenderOption = useMemo(
    () =>
      enableShiftRangeSelect && multiple
        ? shiftRangeRenderOption
        : baseRenderOption,
    [
      baseRenderOption,
      enableShiftRangeSelect,
      multiple,
      shiftRangeRenderOption,
    ],
  );
  const defaultRenderTags = useCallback(
    (
      value: Options[],
      getTagProps: AutocompleteRenderGetTagProps,
      ownerState: AutocompleteOwnerState<Options, Multi, boolean, false>,
    ) => {
      const displayedChips = value.slice(0, limitTags);
      const remainingCount = value.length - limitTags;

      return !ownerState.inputFocused ? (
        <>
          {displayedChips.map((option: OptionType, index) => (
            <StyledChip
              {...getTagProps({ index })}
              key={index}
              label={option.label}
            />
          ))}
          {remainingCount > 0 && <StyledChip label={`+${remainingCount}`} />}
        </>
      ) : (
        value.map((option: OptionType, index) => (
          <StyledChip
            {...getTagProps({ index })}
            key={index}
            label={option.label}
          />
        ))
      );
    },
    [limitTags],
  );
  const handleOnChange = useCallback(
    (event: SyntheticEvent<Element, Event>, newValue: TypedValue) => {
      onChange?.(newValue);
      onChangeCallback?.(newValue);
    },
    [onChange, onChangeCallback],
  );
  const handleInputChange = useCallback(
    (
      _event: SyntheticEvent<Element, Event>,
      newInput: string,
      reason: string,
    ) => {
      // A different query means a different result set — start paging over.
      setVisibleOptionsCount(limitOptions);
      // When keeping the query on select, mirror only genuine typing/clearing
      // into the controlled input; ignore the 'reset' MUI fires on selection so
      // the text stays put.
      if (keepInputOnSelect) {
        if (reason === 'input') setInputValue(newInput);
        else if (reason === 'clear') setInputValue('');
      }
      // Only react to typing, not programmatic resets/selection.
      if (reason === 'input') onInputChange?.(newInput);
    },
    [keepInputOnSelect, limitOptions, onInputChange],
  );
  const handleClose = useCallback(
    () => setVisibleOptionsCount(limitOptions),
    [limitOptions],
  );

  useEffect(() => {
    if (multiple) {
      if (isArray(value) && !loading) {
        const successValuesInOptions = value.filter((el) => {
          return !!options?.filter(
            (option: OptionType) => option.value === (el as Options)?.value,
          );
        }, []);
        onChange?.(successValuesInOptions as TypedValue);
      }
    } else {
      if (isObject(value) && (value as OptionType)?.value && !loading) {
        const isValueInOptions = !!options?.filter(
          (option: OptionType) => option.value === (value as OptionType)?.value,
        ).length;

        if (!isValueInOptions) {
          onChange?.(null as TypedValue);
        }
      }
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, loading]);

  useEffect(() => {
    if (openOnRender) {
      const input = inputRef.current;

      if (input) {
        input.focus({ preventScroll: true });
        setTimeout(() => input.setSelectionRange(0, input.value.length), 0);
      }
    }
  }, [openOnRender]);

  return (
    <Root className={className} fullWidth={fullWidth}>
      {label && showLabel && (
        <Label htmlFor={name} error={!!error}>
          {required && !disabledOrLoading ? (
            <span>
              {label}
              <RequiredMark error={!!error}>*</RequiredMark>
            </span>
          ) : (
            label
          )}
        </Label>
      )}
      <AutoCompleteContainer sizeType={size} readOnly={readOnly}>
        <Autocomplete<Options, Multi, typeof disableClearable, false>
          openOnFocus
          options={options}
          getOptionLabel={
            getOptionLabel ? getOptionLabel : defaultGetOptionLabel
          }
          filterOptions={(availableOptions, state) => {
            // `onInputChange` means the consumer drives the results (typically
            // a server-side search), so they are shown exactly as provided.
            const matched = onInputChange
              ? availableOptions
              : matchOptions(availableOptions, state);
            const shown = Math.min(visibleOptionsCount, matched.length);
            const displayed = onInputChange ? matched : matched.slice(0, shown);

            // Written while rendering on purpose: OptionsListbox renders right
            // after this and reads the counters, and the option click handlers
            // need the list exactly as rendered.
            optionsPagingRef.current = {
              total: matched.length,
              shown: displayed.length,
            };
            displayedOptionsRef.current = displayed;

            return displayed;
          }}
          onInputChange={handleInputChange}
          {...(keepInputOnSelect ? { inputValue } : {})}
          onClose={handleClose}
          ListboxComponent={
            OptionsListbox as unknown as AutocompleteProps<
              Options,
              Multi,
              typeof disableClearable,
              false
            >['ListboxComponent']
          }
          ListboxProps={
            {
              pagingRef: optionsPagingRef,
              pageSize: limitOptions,
              onShowMore: handleShowMoreOptions,
              onScroll: handleListboxScroll,
            } as unknown as AutocompleteProps<
              Options,
              Multi,
              typeof disableClearable,
              false
            >['ListboxProps']
          }
          disableCloseOnSelect={disableCloseOnSelect || keepInputOnSelect}
          loading={loading}
          readOnly={readOnly}
          isOptionEqualToValue={
            isOptionEqualToValue
              ? isOptionEqualToValue
              : defaultIsOptionEqualToValue
          }
          disableClearable={disableClearable || required}
          multiple={multiple}
          value={fullValue as AutocompleteValue<Options, Multi, boolean, false>}
          disabled={disabled}
          fullWidth={fullWidth}
          noOptionsText={noOptionsText}
          limitTags={limitTags}
          getLimitTagsText={
            getLimitTagsText ? getLimitTagsText : defaultGetLimitTagsText
          }
          getOptionDisabled={getOptionDisabled && undefined}
          renderOption={finalRenderOption}
          onChange={handleOnChange}
          renderInput={renderInput ? renderInput : defaultRenderInput}
          renderTags={renderTags ? renderTags : defaultRenderTags}
          groupBy={groupBy}
          renderGroup={renderGroup}
          selectOnFocus
          slotProps={{
            popper: {
              sx: { zIndex: 9998 },
              ...(slotProps?.popper || {}),
            },
            paper: slotProps?.paper,
            clearIndicator: slotProps?.clearIndicator,
            popupIndicator: slotProps?.popupIndicator,
          }}
        />
        {isDefAndNotNull(postfix) && postfix}
      </AutoCompleteContainer>
      {error && showErrorText && (
        <ErrorMessageContainer sizetype={size}>
          <AttentionIcon icon={ICONS_TYPES.Attention} size={20} />
          <ErrorMessage>{error}</ErrorMessage>
        </ErrorMessageContainer>
      )}
    </Root>
  );
};

export { Sizes };
export type {
  Props,
  OptionType,
  SingleAutocompleteResponse,
  MultipleAutocompleteResponse,
};

export default AutoComplete;
